import React, { useState } from 'react'
import { useOnlineUtils, useLocalStorage } from '../../Hooks/offline'
import { Classes } from '@blueprintjs/core'
import { Button, CancelButton, Dialog, Spinner, Card } from '../../Elements'
import { ApolloConsumer } from '@apollo/client'
import { gql } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'

import { JobSchema, HomeownerSchema, EstimateSchema } from '@mmg/validation'
import {
    JOB_FRAGMENT,
    HOMEOWNER_FRAGMENT,
    GET_JOB_QUERY,
    GET_ESTIMATE_QUERY,
    GET_PRESIGN,
    INSERT_DOCUMENTS_MUTATION,
    ESTIMATE_FRAGMENT
} from '../../utils/queries'
import _ from 'lodash'

// move to queries file
const JOB_MUTATION = gql`
    mutation UpdateJob($id: ID!, $job: JobInput!) {
        updateJob(id: $id, job:$job) {
            ...JobFragment
        }
    }
    ${JOB_FRAGMENT}
`
const JOB_CREATE_MUTATION = gql`
    mutation CreateJob($job: JobInput!) {
        createJob(job: $job) {
            id
        }
}`

const ESTIMATE_MUTATION = gql`
    mutation UpdateEstimate($id: ID!, $estimate: EstimateInput!) {
        updateEstimate(id: $id, estimate: $estimate) {
            ...EstimateFragment
        }
    }
    ${ESTIMATE_FRAGMENT}
`

const ESTIMATE_CREATE_MUTATION = gql`
mutation CreateEstimate($estimateInput: EstimateInput!, $isPreliminary: Boolean!) {
    createEstimate(estimateInput: $estimateInput, isPreliminary: $isPreliminary) {
        id
    }
}                                    
`

const HOMEOWNER_MUTATION = gql`
mutation UpdateHomeowner($id: ID!, $homeowner: HomeownerInput!) {
    updateHomeowner(id: $id, homeowner: $homeowner) {
        ...HomeownerFragment
    }
}
${HOMEOWNER_FRAGMENT}
`
const HOMEOWNER_CREATE_MUTATION = gql`
mutation CreateHomeowner($homeowner: HomeownerInput!) {
    createHomeowner(homeowner: $homeowner) {
        id
    }
}
`

// move to queries file
const HOMEOWNER_QUERY = gql`
    ${HOMEOWNER_FRAGMENT}
    query ($id: ID!) {
        homeowner(id: $id) {
            ...HomeownerFragment
        }

    }
`

export const GoOfflineModal = ({ networkError }) => {
    const { showOfflineDialog, setShowOfflineDialog, isOnline, setIsOnline, rawSetNetworkError } = useOnlineUtils()
    const location = useLocation()
    const navigate = useNavigate()

    if (networkError && isOnline) {
        setShowOfflineDialog(true)
        rawSetNetworkError(false)
    }

    return <Dialog
        icon='offline'
        title='No Internet Connection'
        isOpen={showOfflineDialog}
        canOutsideClickClose={false}
        canEscapeKeyClose={false}
        isCloseButtonShown={false}
    >
        <div className={Classes.DIALOG_BODY}>
            <Card>
                Your internet connection was interrupted.  Would you like Insite to go offline or would you like to first reconnect and then retry?
            </Card>
        </div>

        <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px' }}>
            <Button
                onClick={() => {
                    setShowOfflineDialog(false)

                    // potentially track query that needs to be retried
                }}
                intent='warning'
                icon='redo'
            >
                Retry
            </Button>

            <Button
                onClick={() => {
                    setShowOfflineDialog(false)
                    setIsOnline(false)
                    let path = location.pathname
                    if (path.includes('job') || 
                        path.includes('estimate') || 
                        path.includes('customer') || 
                        path.includes('employee') || 
                        path.includes('homeowner')) {
                            
                        } else {
                            navigate('job')
                        }
                }
                }
                icon='offline'
                intent='success'
            >Go Offline
            </Button>

            <CancelButton
                onClick={() => {
                    setShowOfflineDialog(false)
                }
                }>

            </CancelButton>
        </div>


    </Dialog>
}

export const GoOnlineModal = () => {
    const { showGoOnlineDialog, setGoOnlineDialog, setIsOnline, rawSetNetworkError } = useOnlineUtils()
    const { localState, removeOfflineChanges } = useLocalStorage()
    const navigate = useNavigate()

    const [isSyncing, setIsSyncing] = useState()

    return <ApolloConsumer>
        {(client) => <Dialog
            icon='globe-network'
            title='Go Online?'
            isOpen={showGoOnlineDialog}
            canOutsideClickClose={false}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
        >
            <div className={Classes.DIALOG_BODY}>
                <Card>
                    Going back online will provide access to full functionality of the application. You can sync changes made in offline mode or abandon those changes.
                </Card>
            </div>

            {isSyncing && <Spinner />}

            <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px' }}>
                <Button
                    onClick={async () => {
                        // this is where we will handle syncing
                        // then will go online and close the dialog
                        await goOnline(client, localState, setIsSyncing, removeOfflineChanges)
                        setGoOnlineDialog(false)
                        rawSetNetworkError(false)
                        setIsOnline(true)
                        navigate('/job')

                    }}
                    intent='success'>
                    {'Sync & Go Online'}
                </Button>

                <Button
                    onClick={() => {
                        removeOfflineChanges()
                        setGoOnlineDialog(false)
                        setIsOnline(true)
                    }}
                    intent='warning'>
                    {'Abandon & Go Online'}
                </Button>
                <CancelButton
                    onClick={() => {
                        setGoOnlineDialog(false)
                    }
                    }>
                </CancelButton>
            </div>
        </Dialog>}
    </ApolloConsumer>
}

const goOnline = async (client, localState, setIsSyncing, removeOfflineChanges) => {
    setIsSyncing(true)

    let appStorage = localState
    let keys = Object.keys(appStorage)
    let filteredKeys = keys.filter(k => k.indexOf(':') !== -1)

    await Promise.all(filteredKeys.map(async k => {
        const splitKey = k.split(':')
        let endPoint = splitKey.length > 0 ? splitKey[0] : ''
        let id = splitKey.length > 1 ? splitKey[1] : ''

        if (id !== '') {
            switch (endPoint) {
                case 'job':
                    syncJob(client, id, appStorage[k], appStorage[endPoint])
                    break;

                case 'homeowner':
                    syncHomeowner(client, id, appStorage[k], appStorage[endPoint])
                    break;

                case 'estimate':
                    syncEstimate(client, id, appStorage[k], appStorage[endPoint])
                    break

                default:
                    break;
            }
        }
    }))

    await syncDocuments(client, appStorage['documents'])
    removeOfflineChanges()
    setIsSyncing(false)
}

function difference(object, base) {
    function changes(object, base) {
        return _.transform(object, function (result, value, key) {
            if (!_.isEqual(value, base[key])) {
                result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
            }
        });
    }
    return changes(object, base);
}


const syncJob = async (client, id, model, cachedCollection) => {
    let queryResult = await client.query({ query: GET_JOB_QUERY, variables: { id: id } })

    let mutationModel = JobSchema.cast(model, { stripUnknown: true })
    const onlineModel = queryResult && queryResult.data && JobSchema.cast(queryResult.data.job || {}, { stripUnknown: true })
    const cachedModel = JobSchema.cast(cachedCollection.find(s => s.id === id), { stripUnknown: true })

    const onlineProperties = difference(onlineModel, cachedModel)
    mutationModel = _.merge(mutationModel, onlineProperties)

    if (isNaN(id)) {
        delete (mutationModel.id)
        await client.mutate({
            mutation: JOB_CREATE_MUTATION,
            variables: {
                job: {
                    ...mutationModel
                }
            }
        })
    } else {
        await client.mutate({
            mutation: JOB_MUTATION,
            variables: {
                id: id,
                job: {
                    ...mutationModel
                }
            }
        })
    }

}

const syncEstimate = async (client, id, model, cachedCollection) => {
    let queryResult = await client.query({ query: GET_ESTIMATE_QUERY, variables: { id: id } })

    let mutationModel = EstimateSchema.cast(model, { stripUnknown: true })
    const onlineModel = queryResult && queryResult.data && EstimateSchema.cast(queryResult.data.estimate || {}, { stripUnknown: true })
    const cachedModel = EstimateSchema.cast(cachedCollection.find(s => s.id === id), { stripUnknown: true })

    const onlineProperties = difference(onlineModel, cachedModel)
    mutationModel = _.merge(mutationModel, onlineProperties)

    if (isNaN(id)) {
        delete(mutationModel.id)
        await client.mutate({
            mutation: ESTIMATE_CREATE_MUTATION,
            variables: {
                estimate: {
                    ...mutationModel
                }
            }
        })
    } else {
        await client.mutate({
            mutation: ESTIMATE_MUTATION,
            variables: {
                id: id,
                estimate: {
                    ...mutationModel
                }
            }
        })
    }
}

const syncHomeowner = async (client, id, model, cachedCollection) => {
    let queryResult = await client.query({ query: HOMEOWNER_QUERY, variables: { id: id } })

    let mutationModel = HomeownerSchema.cast(model, { stripUnknown: true })
    const onlineModel = queryResult && queryResult.data && HomeownerSchema.cast(queryResult.data.homeowner || {}, { stripUnknown: true })
    const cachedModel = HomeownerSchema.cast(cachedCollection.find(s => s.id === id), { stripUnknown: true })

    const onlineProperties = difference(onlineModel, cachedModel)
    mutationModel = _.merge(mutationModel, onlineProperties)

    if (isNaN(id)) {
        delete(mutationModel.id)
        await client.mutate({
            mutation: HOMEOWNER_CREATE_MUTATION,
            variables: {
                homeowner: {
                    ...mutationModel
                }
            }
        })
    } else {
        await client.mutate({
            mutation: HOMEOWNER_MUTATION,
            variables: {
                id: id,
                homeowner: {
                    ...mutationModel
                }
            }
        })
    }


}

const syncDocuments = async (client, documents) => {
    // add insert logic for documents that don't have 
    const documentsToUpload = documents.filter(document => !document.id)
    let uploadPromises = []

    documentsToUpload.forEach(d => {
        const key = d.key
        const type = d.type


        const promise = client.mutate({
            mutation: GET_PRESIGN,
            variables: {
                key: key,
                contentType: type
            }
        })
        uploadPromises.push(promise)
        promise
            .then(res => {
                let xhr = new XMLHttpRequest()
                xhr.open('PUT', res.data.presign.url, true)
                xhr.setRequestHeader('Content-Type', d.file_data.type)
                xhr.onload = () => {
                    if (xhr.status === 200) {

                        console.log('success')
                    }
                }
                xhr.onerror = () => {
                    console.log('error')
                }
                xhr.send(d.file_data)
            })
            .catch((err) => {

            })
    })

    await Promise.all(uploadPromises).then(async () => {
        let groupedDocuments = _.groupBy(documentsToUpload, (d) => d.job_id)
        let jobIds = Object.keys(groupedDocuments)

        jobIds.forEach(async k => {
            const mappedModel = {
                job_id: k,
                document_file_list: groupedDocuments[k].map(d => ({
                    job_type_job_category: null,
                    file_name: d.file_name,
                    file_type: d.type,
                    key: d.key
                }))
            }

            await client.mutate({
                mutation: INSERT_DOCUMENTS_MUTATION,
                variables: {
                    documents: mappedModel
                }
            }).catch(() => {
            })
        })
    })
}